import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FileText, Download, Eye, X, RefreshCw } from 'lucide-react';
import { buildRctiHtml } from '../utils/rctiDocument';
import { SelfAvatar } from './DriverPhoto';

// Driver-portal "My RCTIs" — lists a driver's archived RCTIs (from getDriverRctis),
// filterable by Period / BAS Quarter / Financial Year, with a summary of Earnings,
// GST and Van Lease Fee, plus inline View and Download-PDF. Network STAFF (whose work
// rolls up into a principal's RCTI) see a note instead of invoices.

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Australian financial year (1 Jul – 30 Jun) from a period-end ISO date.
function fyOf(endISO) {
  const [y, m] = String(endISO || '').split('-').map(Number);
  const start = m >= 7 ? y : y - 1;
  return { start, label: `FY${String(start).slice(2)}-${String(start + 1).slice(2)}` };
}
// BAS quarter from a period-end ISO date.
function quarterOf(endISO) {
  const [, m] = String(endISO || '').split('-').map(Number);
  let label;
  if (m >= 7 && m <= 9) label = 'Jul–Sep';
  else if (m >= 10 && m <= 12) label = 'Oct–Dec';
  else if (m >= 1 && m <= 3) label = 'Jan–Mar';
  else label = 'Apr–Jun';
  const fy = fyOf(endISO);
  return { key: `${fy.label} ${label}`, label: `${label} ${fy.label}`, fy: fy.label };
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const leaseOf = (r) => Math.abs(Number(r?.deductions?.leaseFee) || 0);

async function downloadPdf(r, setBusy) {
  try {
    setBusy(r.invoiceNumber);
    const filename = `${r.companyName || r.personalName || 'RCTI'} ${r.invoiceNumber} ${r.periodLabel || ''}.pdf`.replace(/[\\/:*?"<>|]/g, '-');
    const html = await buildRctiHtml(r);   // identical to the emailed/Xero RCTI
    const res = await fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'rctiHtmlToPdf', html, filename }),
    });
    const data = await res.json();
    if (!data.success || !data.pdfBase64) { alert('Could not generate the PDF — please try again.'); return; }
    const bytes = Uint8Array.from(atob(data.pdfBase64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a'); a.href = url; a.download = data.filename || filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  } catch (e) { alert('Download failed: ' + e.message); }
  finally { setBusy(null); }
}

const VIEWS = [
  { key: 'period', label: 'This Period' },
  { key: 'selperiod', label: 'Select Period' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'selquarter', label: 'Select Quarter' },
  { key: 'fy', label: 'This Financial Year' },
  { key: 'prevfy', label: 'Previous Financial Years' },
];

export default function RctiHistoryView({ driver, name }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [view, setView] = useState('period');
  const [selPeriod, setSelPeriod] = useState('');
  const [selQuarter, setSelQuarter] = useState('');
  const [selFY, setSelFY] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(null);

  // Fetch the driver's archived RCTIs. `manual` drives the Refresh button's own spinner so a
  // re-fetch doesn't blank the whole tab. The component otherwise loads only once on mount,
  // so without this a driver had to fully quit the app to see a freshly-built RCTI.
  const load = useCallback((manual) => {
    if (manual) setRefreshing(true); else setLoading(true);
    return fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDriverRctis', login: driver }),
    })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData({ success: false }))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [driver]);

  useEffect(() => { load(false); }, [load]);

  const rctis = useMemo(() => (data?.rctis || []), [data]);
  const periods = useMemo(() => rctis.map(r => ({ iso: r.periodEndISO, label: r.periodLabel })), [rctis]);
  const quarters = useMemo(() => {
    const seen = {}; rctis.forEach(r => { const q = quarterOf(r.periodEndISO); seen[q.key] = q.label; });
    return Object.entries(seen).map(([key, label]) => ({ key, label }));
  }, [rctis]);
  const prevFYs = useMemo(() => {
    const cur = fyOf(todayISO()).label, seen = new Set();
    rctis.forEach(r => { const f = fyOf(r.periodEndISO).label; if (f !== cur) seen.add(f); });
    return [...seen];
  }, [rctis]);

  // Default the sub-selectors to the newest available option.
  useEffect(() => { if (periods[0] && !selPeriod) setSelPeriod(periods[0].iso); }, [periods, selPeriod]);
  useEffect(() => { if (quarters[0] && !selQuarter) setSelQuarter(quarters[0].key); }, [quarters, selQuarter]);
  useEffect(() => { if (prevFYs[0] && !selFY) setSelFY(prevFYs[0]); }, [prevFYs, selFY]);

  const shown = useMemo(() => {
    if (!rctis.length) return [];
    if (view === 'period') return rctis.slice(0, 1);
    if (view === 'selperiod') return rctis.filter(r => r.periodEndISO === selPeriod);
    if (view === 'quarter') { const k = quarterOf(todayISO()).key; return rctis.filter(r => quarterOf(r.periodEndISO).key === k); }
    if (view === 'selquarter') return rctis.filter(r => quarterOf(r.periodEndISO).key === selQuarter);
    if (view === 'fy') { const k = fyOf(todayISO()).label; return rctis.filter(r => fyOf(r.periodEndISO).label === k); }
    if (view === 'prevfy') return rctis.filter(r => fyOf(r.periodEndISO).label === selFY);
    return [];
  }, [rctis, view, selPeriod, selQuarter, selFY]);

  const summary = useMemo(() => shown.reduce((a, r) => ({
    earnings: a.earnings + (Number(r.grossIncome) || 0),
    gst: a.gst + (Number(r.gst) || 0),
    lease: a.lease + leaseOf(r),
    total: a.total + (Number(r.total) || 0),
  }), { earnings: 0, gst: 0, lease: 0, total: 0 }), [shown]);

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 14 };

  if (loading) return <div style={{ padding: 24, color: 'var(--hp-dim)' }}><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading your RCTIs…</div>;

  // Network staff: their work is invoiced under the principal — no individual RCTI.
  if (data?.isNetworkMember) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ ...card, textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
          <FileText size={28} color="var(--hp-gold)" style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 700, marginBottom: 6 }}>No individual RCTI</div>
          <div style={{ color: 'var(--hp-dim)', fontSize: '.86rem', lineHeight: 1.5 }}>
            Your deliveries are invoiced under your network principal{data.principal ? <strong> {data.principal}</strong> : ''}.
            The RCTI for this work sits on their account, not here.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 760, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <SelfAvatar login={driver} size={34} editable={false} />
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} color="var(--hp-gold)" /> My RCTIs
          </h3>
          {name && <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>{name}</div>}
        </div>
        <button onClick={() => load(true)} disabled={refreshing || loading} title="Refresh — pull the latest RCTIs"
          style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: '.74rem',
            padding: '6px 11px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent',
            color: 'var(--hp-dim)', cursor: (refreshing || loading) ? 'default' : 'pointer',
          }}>
          <RefreshCw size={13} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* View selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            style={{
              fontSize: '.74rem', padding: '6px 11px', borderRadius: 20, cursor: 'pointer',
              border: '1px solid', borderColor: view === v.key ? 'var(--hp-gold)' : 'var(--hp-border)',
              background: view === v.key ? 'rgba(201,168,76,.15)' : 'transparent',
              color: view === v.key ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: view === v.key ? 700 : 500,
            }}>{v.label}</button>
        ))}
      </div>

      {/* Sub-selectors */}
      {view === 'selperiod' && (
        <select value={selPeriod} onChange={e => setSelPeriod(e.target.value)} style={{ marginBottom: 12, padding: '7px 10px', borderRadius: 8, width: '100%', maxWidth: 320 }}>
          {periods.map(p => <option key={p.iso} value={p.iso}>{p.label}</option>)}
        </select>
      )}
      {view === 'selquarter' && (
        <select value={selQuarter} onChange={e => setSelQuarter(e.target.value)} style={{ marginBottom: 12, padding: '7px 10px', borderRadius: 8, width: '100%', maxWidth: 320 }}>
          {quarters.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}
        </select>
      )}
      {view === 'prevfy' && (
        prevFYs.length
          ? <select value={selFY} onChange={e => setSelFY(e.target.value)} style={{ marginBottom: 12, padding: '7px 10px', borderRadius: 8, width: '100%', maxWidth: 320 }}>
              {prevFYs.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          : <div style={{ marginBottom: 12, color: 'var(--hp-dim)', fontSize: '.82rem' }}>No previous financial years yet.</div>
      )}

      {/* Summary */}
      {/* Four fixed columns didn't fit a phone: the tiles pushed the whole tab wider than
          the screen, taking the Refresh button off the right edge with them. */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(140px, 100%), 1fr))`, gap: 8, marginBottom: 14 }}>
        <div style={card}><div style={{ fontSize: '.64rem', textTransform: 'uppercase', color: 'var(--hp-dim)', letterSpacing: '.06em' }}>Earnings (ex GST)</div><div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{fmt(summary.earnings)}</div></div>
        <div style={card}><div style={{ fontSize: '.64rem', textTransform: 'uppercase', color: 'var(--hp-dim)', letterSpacing: '.06em' }}>GST</div><div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{fmt(summary.gst)}</div></div>
        {summary.lease > 0 && <div style={card}><div style={{ fontSize: '.64rem', textTransform: 'uppercase', color: 'var(--hp-dim)', letterSpacing: '.06em' }}>Van Lease Fee</div><div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#c00' }}>-{fmt(summary.lease)}</div></div>}
        <div style={{ ...card, background: 'rgba(201,168,76,.08)' }}><div style={{ fontSize: '.64rem', textTransform: 'uppercase', color: 'var(--hp-dim)', letterSpacing: '.06em' }}>Total Payable (inc GST)</div><div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--hp-gold)' }}>{fmt(summary.total)}</div></div>
      </div>

      {/* RCTI list */}
      {shown.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--hp-dim)' }}>No RCTIs for this selection.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map(r => (
            <div key={r.invoiceNumber + r.periodEndISO} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)', fontSize: '.82rem' }}>{r.invoiceNumber}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{r.periodLabel}</div>
              </div>
              <div style={{ textAlign: 'right', minWidth: 110 }}>
                <div style={{ fontWeight: 700 }}>{fmt(r.total)}</div>
                <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>inc GST</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => { setBusy(r.invoiceNumber); buildRctiHtml(r).then(h => { setPreviewHtml(h); setPreview(r); setBusy(null); }); }} disabled={busy === r.invoiceNumber} title="View" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.74rem', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', cursor: 'pointer' }}><Eye size={13} /> View</button>
                <button onClick={() => downloadPdf(r, setBusy)} disabled={busy === r.invoiceNumber} title="Download PDF" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.74rem', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,.12)', color: 'var(--hp-gold)', cursor: 'pointer', fontWeight: 700 }}>
                  {busy === r.invoiceNumber ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inline view modal */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflow: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, maxWidth: 760, width: '100%', position: 'relative' }}>
            <button onClick={() => setPreview(null)} style={{ position: 'absolute', top: 10, right: 10, background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', zIndex: 1 }}><X size={14} /></button>
            <iframe title={`RCTI ${preview.invoiceNumber}`} srcDoc={previewHtml} style={{ width: '100%', height: '78vh', border: 'none', borderRadius: 10, background: '#fff', display: 'block' }} />
            <div style={{ textAlign: 'center', padding: '0 0 16px' }}>
              <button onClick={() => downloadPdf(preview, setBusy)} style={{ fontSize: '.8rem', padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1a3a6b', color: '#fff', cursor: 'pointer', fontWeight: 700 }}><Download size={13} /> Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
